import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Upload, Video, Image as ImageIcon, ArrowRight, Trash2, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { useSubscription } from '../hooks/useSubscription';
import UpgradeModal from '../components/UpgradeModal';

const BusinessCreate = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { canCreateBusiness, loading: subLoading } = useSubscription();
  const [showUpgrade, setShowUpgrade] = useState(false);

  useEffect(() => {
    if (!subLoading && !canCreateBusiness) setShowUpgrade(true);
  }, [subLoading, canCreateBusiness]);

  const [form, setForm] = useState({
    name: '', tagline: '', description: '', category: '', city: '', state: '', funding_target: '',
  });
  const [coverFile, setCoverFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const coverInputRef = useRef();
  const videoInputRef = useRef();

  const uploadFile = async (file, bucket) => {
    const ext = file.name.split('.').pop();
    const path = `${user.id}/${Date.now()}.${ext}`;
    const { error: upErr } = await supabase.storage.from(bucket).upload(path, file, { upsert: false });
    if (upErr) throw upErr;
    const { data } = supabase.storage.from(bucket).getPublicUrl(path);
    return data.publicUrl;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;

    // Defensive validation (trim + min length)
    const name = (form.name || '').trim();
    if (!name || name.length < 2) {
      setError('Dê um nome para o seu negócio (mínimo 2 caracteres).');
      return;
    }

    // Plan limit guard
    if (!canCreateBusiness) {
      setShowUpgrade(true);
      return;
    }

    setError(''); setUploading(true);
    try {
      let cover_image_url = null, video_url = null;
      if (coverFile) cover_image_url = await uploadFile(coverFile, 'images');
      if (videoFile) video_url = await uploadFile(videoFile, 'videos');

      const { error: insErr } = await supabase.from('businesses').insert({
        owner_id: user.id,
        name,
        tagline: (form.tagline || '').trim(),
        description: (form.description || '').trim(),
        category: (form.category || '').trim(),
        city: (form.city || '').trim(),
        state: (form.state || '').trim(),
        funding_target: parseFloat(form.funding_target) || 0,
        cover_image_url,
        video_url,
        is_seeking: true,
      });
      if (insErr) throw insErr;
      navigate('/app/feed');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[BusinessCreate] insert error', err);
      setError(err.message || 'Erro ao salvar.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
      <div className="mb-6">
        <span className="eyebrow eyebrow-gold"><Sparkles size={12}/> Publicar negócio</span>
        <h1 className="heading-display text-3xl mt-3">Conte sua história e <span className="gradient-text-gold">acenda a Spark</span></h1>
        <p className="mt-2 text-sm text-slate-400">Investidores vão ver sua ideia aqui no feed.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Cover image */}
        <div className="glass-card rounded-2xl p-4">
          <label className="text-[10px] uppercase tracking-widest text-slate-400 mb-2 block">Foto de capa</label>
          {coverFile ? (
            <div className="relative aspect-video rounded-xl overflow-hidden">
              <img src={URL.createObjectURL(coverFile)} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => setCoverFile(null)} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 flex items-center justify-center"><Trash2 size={14}/></button>
            </div>
          ) : (
            <button type="button" onClick={() => coverInputRef.current?.click()} className="w-full aspect-video rounded-xl border-2 border-dashed border-white/15 hover:border-[#4facfe]/50 flex flex-col items-center justify-center gap-2 text-slate-400 hover:text-white transition">
              <ImageIcon size={24} />
              <span className="text-xs">Clique para enviar uma foto</span>
            </button>
          )}
          <input ref={coverInputRef} type="file" accept="image/*" hidden onChange={(e) => setCoverFile(e.target.files[0])} />
        </div>

        {/* Video */}
        <div className="glass-card rounded-2xl p-4">
          <label className="text-[10px] uppercase tracking-widest text-slate-400 mb-2 block">Vídeo do negócio (estilo Reels) — opcional</label>
          {videoFile ? (
            <div className="relative aspect-video rounded-xl overflow-hidden bg-black">
              <video src={URL.createObjectURL(videoFile)} controls className="w-full h-full" />
              <button type="button" onClick={() => setVideoFile(null)} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 flex items-center justify-center"><Trash2 size={14}/></button>
            </div>
          ) : (
            <button type="button" onClick={() => videoInputRef.current?.click()} className="w-full aspect-video rounded-xl border-2 border-dashed border-white/15 hover:border-[#ffa726]/50 flex flex-col items-center justify-center gap-2 text-slate-400 hover:text-white transition">
              <Video size={24} />
              <span className="text-xs">Clique para enviar um vídeo (até 100MB)</span>
            </button>
          )}
          <input ref={videoInputRef} type="file" accept="video/*" hidden onChange={(e) => {
            const f = e.target.files[0];
            if (f && f.size > 100*1024*1024) { setError('Vídeo muito grande (máx 100MB)'); return; }
            setVideoFile(f);
          }} />
        </div>

        <div className="glass-card rounded-2xl p-4 space-y-3">
          <Field testid="business-name" label="Nome do negócio" value={form.name} onChange={(v) => setForm({...form, name: v})} required placeholder="Ex: Cafeteria Beatriz" />
          <Field testid="business-tagline" label="Frase de impacto" value={form.tagline} onChange={(v) => setForm({...form, tagline: v})} placeholder="Ex: A cafeteria mais charmosa do bairro" />
          <Field testid="business-description" label="Conte sua história" value={form.description} onChange={(v) => setForm({...form, description: v})} textarea placeholder="O que torna seu negócio especial?" />
          <div className="grid grid-cols-2 gap-3">
            <Field testid="business-city" label="Cidade" value={form.city} onChange={(v) => setForm({...form, city: v})} placeholder="São Paulo" />
            <Field testid="business-state" label="Estado" value={form.state} onChange={(v) => setForm({...form, state: v})} placeholder="SP" />
          </div>
          <Field testid="business-funding-target" label="Quanto procura captar (R$)" type="number" value={form.funding_target} onChange={(v) => setForm({...form, funding_target: v})} placeholder="80000" />
          <Field testid="business-category" label="Categoria" value={form.category} onChange={(v) => setForm({...form, category: v})} placeholder="Alimentação, Tecnologia, Moda..." />
        </div>

        {error && <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="business-create-error">{error}</div>}

        <button type="submit" disabled={uploading} data-testid="business-create-submit" className="w-full btn-gold rounded-xl py-3 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50">
          {uploading ? 'Publicando...' : <>Publicar no Feed <ArrowRight size={14} /></>}
        </button>
      </form>
      <UpgradeModal open={showUpgrade} onClose={() => setShowUpgrade(false)} reason="business_limit" />
    </div>
  );
};

const Field = ({ label, value, onChange, textarea, required, type='text', placeholder, testid }) => {
  const inputId = testid || `field-${label}`.replace(/\s+/g, '-').toLowerCase();
  return (
    <div>
      <label htmlFor={inputId} className="text-[10px] uppercase tracking-widest text-slate-400 mb-1.5 block">{label}{required && ' *'}</label>
      {textarea ? (
        <textarea
          id={inputId}
          name={inputId}
          data-testid={testid}
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          placeholder={placeholder}
          className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#4facfe]/50 resize-none"
        />
      ) : (
        <input
          id={inputId}
          name={inputId}
          data-testid={testid}
          required={required}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#4facfe]/50"
        />
      )}
    </div>
  );
};

export default BusinessCreate;
